import { zodResolver } from '@hookform/resolvers/zod';
import type { input, output } from 'zod';
import { Stack, TextField } from '@mui/material';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { DialogType } from '../../../../@types';
import { Partner } from '../../../../@types/api';
import { QUERY_KEYS } from '../../../../constants';
import { getRoleOptions, roleRequiresManager, USER_ROLES } from '../../../../constants/keycloak';
import { useModal } from '../../../../contexts/ModalContext';
import { useInvalidateQuery } from '../../../../hooks/useInvalidateQuery';
import { useMarketplace } from '../../../../hooks/useMarketplace';
import useMutateData from '../../../../hooks/useMutateData';
import usePermissions from '../../../../hooks/usePermissions';
import { createPartnerSchema, editPartnerSchema, PartnerFormData } from '../../../../schema';
import CollapsibleSection from '../../../atoms/CollapsibleSection';
import FormFieldStack from '../../../atoms/FormFieldStack';
import Switch from '../../../atoms/Switch';
import FormModalLayout from '../../../layouts/FormModalLayout';
import FormSelect from '../../../molecules/FormSelect';
import PartnerSelect from '../../../molecules/PartnerSelect';
import PasswordFields from '../../../molecules/PasswordFields';

export const DEFAULT_PARTNER_DATA = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
  groupName: undefined,
  managerId: undefined,
} satisfies Partial<PartnerFormData>;

const PartnerForm = ({ closeModal }: DialogType) => {
  const { item: partner } = useModal<Partner>();
  const { role, isManager } = usePermissions();
  const { userId: loggedInUserId } = useAuth();
  const { hasAffiliateSystem } = useMarketplace();
  const invalidateData = useInvalidateQuery();
  const { isStandaloneCasino } = useMarketplace();

  const isEdit = !!partner;
  const schema = isEdit ? editPartnerSchema : createPartnerSchema(isManager);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<input<typeof schema>, unknown, output<typeof schema>>({
    defaultValues: DEFAULT_PARTNER_DATA,
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (isEdit) {
      reset({
        ...partner,
        groupName: partner.role.name,
        managerId: partner.teamLead?.userId,
        products: {
          sportsbook: partner.products?.sportsbook ?? true,
          casino: partner.products?.casino ?? true,
        },
      });
    }
  }, [partner, isEdit, reset]);

  const { createData, updateData } = useMutateData('agent', [QUERY_KEYS.partners], 'crm');

  const handleClose = () => {
    closeModal?.();
    reset(DEFAULT_PARTNER_DATA);
  };

  const onFormSubmitSuccess = () => {
    handleClose();
    invalidateData([QUERY_KEYS.agentSearch]);
  };

  const onFormSubmit = (formData: output<typeof schema>) => {
    if (isEdit) {
      const { groupName, products, ...restPayload } = formData;
      const updatePayload = groupName === USER_ROLES.AGENT ? { ...restPayload, products } : restPayload;

      updateData(partner.id, updatePayload, onFormSubmitSuccess);
      return;
    }

    const createFormData = createPartnerSchema(isManager).parse(formData);
    let managerId;
    const selectedRole = createFormData.groupName;

    if (isManager) {
      managerId = loggedInUserId;
    } else if (roleRequiresManager(selectedRole)) {
      managerId = createFormData.managerId || undefined;
    }

    const { confirmPassword, products, ...restPayload } = createFormData;
    const createPayload = selectedRole === USER_ROLES.AGENT ? { ...restPayload, products } : restPayload;

    createData({ ...createPayload, managerId }, onFormSubmitSuccess);
  };

  const roleOptions = useMemo(() => {
    const options = getRoleOptions(role);

    if (hasAffiliateSystem) {
      return options;
    }

    return options.filter((option) => option.id !== USER_ROLES.AFFILIATE);
  }, [role, hasAffiliateSystem]);

  const selectedRole = watch('groupName');
  const isAgentRole = selectedRole === USER_ROLES.AGENT;
  const showManagerSelect = roleRequiresManager(selectedRole) && !isManager;
  const displayProductsSection = isAgentRole && isEdit && !isStandaloneCasino;

  return (
    <FormModalLayout
      onSave={handleSubmit(onFormSubmit)}
      label={isEdit ? 'Edit partner' : 'Add new partner'}
      onClose={handleClose}
      isEdit={isEdit}
    >
      <TextField
        label='First Name'
        {...register('firstName')}
        error={!!errors.firstName}
        helperText={errors.firstName?.message}
        required
      />
      <TextField
        label='Last Name'
        {...register('lastName')}
        error={!!errors.lastName}
        helperText={errors.lastName?.message}
        required
      />
      <PasswordFields resetPassword={isEdit} errors={errors} register={register} />
      <TextField
        label='Email'
        {...register('email')}
        error={!!errors.email}
        helperText={errors.email?.message}
        required
      />
      <FormSelect
        control={control}
        label='Role'
        name='groupName'
        options={roleOptions}
        required
        error={errors.groupName}
        disabled={isEdit}
      />
      {showManagerSelect && (
        <PartnerSelect<input<typeof schema>, output<typeof schema>>
          control={control}
          label='Manager'
          name='managerId'
          queryKey={[QUERY_KEYS.partners]}
          queryParams={{
            roleTypes: ['Manager'],
          }}
          required
          error={errors.managerId}
          disabled={isEdit}
          multipleSelect={false}
        />
      )}
      {displayProductsSection && (
        <CollapsibleSection title='Products'>
          <Stack spacing={1}>
            <FormFieldStack label='Sportsbook'>
              <Controller
                name='products.sportsbook'
                control={control}
                render={({ field }) => <Switch {...field} value={field.value ?? false} ref={null} />}
              />
            </FormFieldStack>
            <FormFieldStack label='Casino'>
              <Controller
                name='products.casino'
                control={control}
                render={({ field }) => <Switch {...field} value={field.value ?? false} ref={null} />}
              />
            </FormFieldStack>
          </Stack>
        </CollapsibleSection>
      )}
    </FormModalLayout>
  );
};

export default PartnerForm;
